import { useState, useEffect } from 'react';
import { associateProperty } from '../../services/callService';
import { searchClients } from '../../services/clientService';
import { searchProperties } from '../../services/propertyService';
import type { Client } from '../../types/client';
import type { Property } from '../../types/property';

interface AssociatePropertyModalProps {
  callId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AssociatePropertyModal({ callId, onClose, onSuccess }: AssociatePropertyModalProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    searchClients({}, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setClients);
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setProperties([]);
      return;
    }
    searchProperties({ clientId: selectedClientId }, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setProperties);
  }, [selectedClientId]);

  function handleClientChange(newClientId: string) {
    setSelectedClientId(newClientId);
    setSelectedPropertyId('');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPropertyId) return;

    setError('');
    setLoading(true);
    try {
      await associateProperty(callId, selectedPropertyId);
      onSuccess();
    } catch {
      setError('Failed to associate property');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Associate Property</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Client *</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  required
                  className={inputClass}
                >
                  <option value="">Select a client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Property *</label>
                <select
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  disabled={!selectedClientId}
                  required
                  className={`${inputClass} ${!selectedClientId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">{selectedClientId ? 'Select a property' : 'Select a client first'}</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedPropertyId}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Associating...' : 'Associate Property'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
